import React, { useState } from 'react';
import { Table, Button, Tag, Space, Input, Card, Empty, Typography } from 'antd';
import { useToast } from '../../../contexts/ToastContext';
import { SearchOutlined, CheckCircleOutlined, ClockCircleOutlined, StarOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { menteeProfileQueryKeys } from '../../../lib/queryClient';
import ReferenceApprovalModal from '../../../components/Common/ReferenceApprovalModal';

const { Title, Text } = Typography;
const { Search } = Input;

interface IncomingReferenceRequest {
  id: number;
  content: string;
  state: boolean;
  createdAt: string;
  mentorName: string;
  mentorId?: number;
  menteeId?: number;
  mentorBookingId?: number;
  bootcampApplicationId?: number;
}

const IncomingReferenceRequestsTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<IncomingReferenceRequest | null>(null);

  const { data: referenceRequests = [], isLoading: loading, refetch } = useQuery({
    queryKey: menteeProfileQueryKeys.incomingReferenceRequests,
    queryFn: async () => {
      const res = await apiService.getMenteeIncomingReferenceRequests();
      if (!res.success) throw new Error(res.message || 'Referans talepleri yüklenemedi.');
      return res.data;
    },
  });

  const handleApproveReference = (request: IncomingReferenceRequest) => {
    setSelectedRequest(request);
    setModalVisible(true);
  };

  const handleModalSuccess = () => {
    toast.success('İşlem başarıyla tamamlandı.');
    queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.incomingReferenceRequests });
  };

  const getStatusColor = (state: boolean) => {
    return state ? 'green' : 'orange';
  };

  const getStatusIcon = (state: boolean) => {
    return state ? <CheckCircleOutlined /> : <ClockCircleOutlined />;
  };

  const getStatusText = (state: boolean) => {
    return state ? 'Onaylandı' : 'Bekliyor';
  };

  const getTypeText = (mentorBookingId?: number, bootcampApplicationId?: number) => {
    if (mentorBookingId) return '1-1 Görüşme';
    if (bootcampApplicationId) return 'Bootcamp';
    return 'Bilinmiyor';
  };

  const getTypeColor = (mentorBookingId?: number, bootcampApplicationId?: number) => {
    if (mentorBookingId) return 'blue';
    if (bootcampApplicationId) return 'green';
    return 'default';
  };

  const filteredRequests = referenceRequests.filter(request =>
    request.mentorName.toLowerCase().includes(searchText.toLowerCase()) ||
    request.content.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Mentor',
      dataIndex: 'mentorName',
      key: 'mentorName',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: 'Tip',
      key: 'type',
      render: (record: IncomingReferenceRequest) => (
        <Tag color={getTypeColor(record.mentorBookingId, record.bootcampApplicationId)}>
          {getTypeText(record.mentorBookingId, record.bootcampApplicationId)}
        </Tag>
      )
    },
    {
      title: 'Talep İçeriği',
      dataIndex: 'content',
      key: 'content',
      render: (text: string) => (
        <Text ellipsis={{ tooltip: text }} style={{ maxWidth: 200 }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Durum',
      dataIndex: 'state',
      key: 'state',
      render: (state: boolean) => (
        <Tag color={getStatusColor(state)} icon={getStatusIcon(state)}>
          {getStatusText(state)}
        </Tag>
      )
    },
    {
      title: 'Tarih',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR')
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (record: IncomingReferenceRequest) => (
        <Space>
          {!record.state && (
            <Button
              type="primary"
              icon={<StarOutlined />}
              onClick={() => handleApproveReference(record)}
            >
              Referans Onayla
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Title level={4} style={{ margin: 0 }}>Gelen Referans Talepleri</Title>
          <Button onClick={() => refetch()} loading={loading}>
            Yenile
          </Button>
        </div>
        
        <Search
          placeholder="Mentor adı veya talep içeriğinde ara..."
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ marginBottom: '16px' }}
          prefix={<SearchOutlined />}
        />
      </Card>

      {filteredRequests.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={searchText ? "Arama kriterlerinize uygun talep bulunamadı" : "Henüz gelen referans talebiniz bulunmuyor"}
          style={{ marginTop: '50px' }}
        />
      ) : (
        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={filteredRequests}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} talep`,
          }}
        />
      )}

      <ReferenceApprovalModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSuccess={handleModalSuccess}
        referenceRequest={selectedRequest}
        userType="mentee"
      />
    </div>
  );
};

export default IncomingReferenceRequestsTab;
